import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';

import { Button, FontAwesomeIcon } from '@appcharge/shared-ui';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { EBadgeType, Offer, RollingOfferProductSequence } from 'common/contracts';
import { MIN_ROLLING_SUB_OFFERS } from 'constants/constants';
import { v4 as uuidv4 } from 'uuid';
import { RollingOfferFormFields } from 'views/Offers/RollingOffers/Form/RollingOfferForm.consts';

import RichText from 'components/RichText/RichText';
import { SortableItem } from 'components/SortableItem';

import SubOfferCard from './Card';

const CONFIGURATION_DESCRIPTION =
  'Set 1 or more sub offers Rolling Offer. Each sub offer can contain up to 3 products.';

interface SubOfferWithId extends RollingOfferProductSequence {
  _sortId?: string;
}

interface ISubOffersListProps {
  rollingOfferId?: string;
  productsSequence: RollingOfferProductSequence[];
  setProductsSequence: React.Dispatch<
    React.SetStateAction<RollingOfferProductSequence[]>
  >;
  setBarPointsValidity?: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  relevantBadgeType?: EBadgeType;
  progressBarOffers?: Offer[];
}

const SubOffersList: React.FC<ISubOffersListProps> = ({
  rollingOfferId,
  productsSequence,
  setProductsSequence,
  setBarPointsValidity,
  relevantBadgeType,
  progressBarOffers
}): React.ReactElement => {
  const { setValue } = useFormContext<RollingOfferFormFields>();

  const [openCardIndex, setOpenCardIndex] = useState<number | undefined>(
    undefined
  );
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const itemsWithIds = useMemo((): SubOfferWithId[] => {
    return productsSequence.map((item, idx) => ({
      ...item,
      _sortId:
        (item as SubOfferWithId)._sortId || `sub-offer-${idx}-${uuidv4()}`
    }));
  }, [productsSequence]);

  const sortableIds = useMemo(
    (): string[] => itemsWithIds.map((item) => item._sortId!),
    [itemsWithIds]
  );

  useEffect(() => {
    const needsIds = productsSequence.some(
      (item) => !(item as SubOfferWithId)._sortId
    );
    if (needsIds && productsSequence.length > 0) {
      const withIds = productsSequence.map((item, idx) => ({
        ...item,
        _sortId:
          (item as SubOfferWithId)._sortId || `sub-offer-${idx}-${uuidv4()}`
      }));
      setProductsSequence(withIds);
    }

    return () => setOpenCardIndex(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent): void => {
      const id = String(event.active.id);
      const index = itemsWithIds.findIndex((item) => item._sortId === id);
      setActiveId(id);
      setActiveIndex(index);
      setOpenCardIndex(undefined);
    },
    [itemsWithIds]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent): void => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        setActiveId(null);
        setActiveIndex(null);
        return;
      }

      const oldIndex = itemsWithIds.findIndex(
        (item) => item._sortId === String(active.id)
      );
      const newIndex = itemsWithIds.findIndex(
        (item) => item._sortId === String(over.id)
      );

      if (oldIndex === -1 || newIndex === -1) {
        setActiveId(null);
        setActiveIndex(null);
        return;
      }

      const newSequence = arrayMove(itemsWithIds, oldIndex, newIndex).map(
        (item, index) => ({ ...item, index: index + 1 })
      );

      setProductsSequence(newSequence);
      setValue('productsSequence', newSequence, { shouldDirty: true });

      setOpenCardIndex((prev) => {
        if (prev === undefined) {
          return undefined;
        }
        if (prev === oldIndex) {
          return newIndex;
        }
        if (oldIndex < prev && prev <= newIndex) {
          return prev - 1;
        }
        if (newIndex <= prev && prev < oldIndex) {
          return prev + 1;
        }
        return prev;
      });

      setActiveId(null);
      setActiveIndex(null);
    },
    [itemsWithIds, setProductsSequence, setValue]
  );

  const handleDragCancel = useCallback((): void => {
    setActiveId(null);
    setActiveIndex(null);
  }, []);

  const handleToggleExpand = useCallback((index: number): void => {
    setOpenCardIndex((prev) => {
      if (prev === index) {
        return undefined;
      }
      return index;
    });
  }, []);

  const handleDelete = useCallback(
    (index: number): void => {
      if (itemsWithIds.length <= MIN_ROLLING_SUB_OFFERS) {
        return;
      }

      const filtered = itemsWithIds.filter((_, i) => i !== index);
      const newSequence = filtered.map((item, i) => ({
        ...item,
        index: i + 1
      }));

      setProductsSequence(newSequence);
      setValue('productsSequence', newSequence, { shouldDirty: true });

      setOpenCardIndex((prev) => {
        if (prev === undefined) {
          return undefined;
        }
        if (prev === index) {
          return undefined;
        }
        if (prev > index) {
          return prev - 1;
        }
        return prev;
      });
    },
    [itemsWithIds, setProductsSequence, setValue]
  );

  const handleAddSubOffer = useCallback((): void => {
    const nextIndex = itemsWithIds.length + 1;
    const newSubOffer: SubOfferWithId = {
      index: nextIndex,
      priceInUsdCents: 0,
      displayName: '',
      products: [{ product: { images: [] } }],
      _sortId: `sub-offer-new-${uuidv4()}`
    };
    const newSequence = [...itemsWithIds, newSubOffer];
    setProductsSequence(newSequence);
    setValue('productsSequence', newSequence, { shouldDirty: true });
  }, [itemsWithIds, setProductsSequence, setValue]);

  return (
    <div className="flex flex-col gap-9 pb-10">
      <RichText title="Configuration" description={CONFIGURATION_DESCRIPTION} />

      <Button
        type="button"
        variant="secondary"
        onClick={handleAddSubOffer}
        className="max-w-[186px] bg-base-muted border-none h-10 gap-0 hover:bg-violet-100 shadow-none"
      >
        <FontAwesomeIcon
          icon="fa-regular fa-circle-plus"
          className="mr-2 text-base"
        />
        Add New Sub Offer
      </Button>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          <div className="flex flex-col gap-2">
            {itemsWithIds.map((item, index) => (
              <SortableItem key={item._sortId} id={item._sortId!}>
                <SubOfferCard
                  rollingOfferId={rollingOfferId}
                  productSequenceItem={item}
                  sequenceIndex={index}
                  productsSequence={itemsWithIds}
                  setProductsSequence={setProductsSequence}
                  setBarPointsValidity={setBarPointsValidity}
                  relevantBadgeType={relevantBadgeType}
                  isExpanded={openCardIndex === index}
                  isCurrentItemDragged={activeId === item._sortId}
                  onToggleExpand={() => handleToggleExpand(index)}
                  onDelete={() => handleDelete(index)}
                  progressBarOffers={progressBarOffers}
                />
              </SortableItem>
            ))}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeId && activeIndex !== null && itemsWithIds[activeIndex] && (
            <div className="cursor-grabbing shadow-lg bg-white z-50 rounded-lg">
              <SubOfferCard
                rollingOfferId={rollingOfferId}
                productSequenceItem={itemsWithIds[activeIndex]}
                sequenceIndex={activeIndex}
                productsSequence={itemsWithIds}
                setProductsSequence={setProductsSequence}
                setBarPointsValidity={setBarPointsValidity}
                relevantBadgeType={relevantBadgeType}
                isExpanded={false}
                isCurrentItemDragged={true}
                onToggleExpand={() => {}}
                onDelete={() => {}}
                progressBarOffers={progressBarOffers}
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
};

export default SubOffersList;
